import React, { useState, useContext } from 'react';
import styled, { css } from 'styled-components';

import { ReactComponent as Arrow } from '../../common/icons/arrow.svg';
import { ReactComponent as Close } from '../../common/icons/close.svg';
import { ReactComponent as DiscountIcon } from '../../common/icons/discount.svg';
import discount_bg from '../../common/icons/discount_bg.svg';
import { withApplicationUrl } from '../../common/utils/with-application-url';
import { validateDiscountCode } from '../api';
import Spinner from './elements/Spinner';
import { deleteCookie } from '../utils/cookies';
import CartContext from '../contexts/CartContext';
import DiscountInfo, {
  DiscountInfoGrid,
  getDiscount,
  getTotalSpending,
} from './elements/DiscountInfo';
import handleConfirmation from '../utils/adaHandler';
import { logInfo } from '../utils/logging';

const dicountBgUrl = withApplicationUrl(discount_bg);

const DiscountCodeStyle = styled.form.attrs({
  className: 'DiscountScreen-Container',
})`
  background-color: #f9f9f9;
  height: 100vh;
  overflow: hidden;
  margin: -8px;
  background-image: url(${dicountBgUrl});
  background-repeat: no-repeat;
  background-position: 130% 120%;
  line-height: normal;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const DiscountCloseWrapper = styled.span`
  ${({ size }) =>
    css`
      height: ${size}px;
      width: ${size}px;
    `}
`;

const DiscountClose = ({ className, onClick, size }) => {
  return (
    <DiscountCloseWrapper
      data-cy="discount-close"
      size={size}
      tabIndex={0}
      onKeyPress={handleConfirmation(onClick)}
    >
      <Close onClick={onClick} className={className} />
    </DiscountCloseWrapper>
  );
};

const Heading = styled(({ className, handleClose, children }) => {
  return (
    <div className={`${className} DiscountScreen-Heading`}>
      <DiscountIcon />
      {children}
      <DiscountClose
        size={14}
        onClick={handleClose}
        className="DiscountScreen-Close"
      />
    </div>
  );
})`
  display: grid;
  grid-template-columns: 30px auto 20px;
  padding: 17px 20px;
  font-weight: 600;
  font-size: 18px;
  background-color: #fff;
  border-bottom: 1px solid #e6e6e6;

  span > svg {
    cursor: pointer;
  }
`;

const Input = styled.input.attrs({
  className: 'DiscountScreen-Input',
})`
  border-radius: 3px;
  padding: 7px 14px;
  border: 1px solid #c8c8c8;

  &:focus {
    border: 1px solid #50b83c;
    outline: none;
  }

  ${(props) =>
    props.hasError &&
    css`
      border: 1px solid #e53b3a;
    `}

  ${(props) =>
    props.disabled &&
    css`
      border: 1px solid #c8c8c8;
      background: #fff;
    `}
`;

const DiscountContent = styled.div.attrs({
  className: 'DiscountScreen-Content',
})`
  padding: 0 20px 10px 20px;
  height: inherit;
  ${DiscountInfoGrid} {
    align-items: end;
    justify-items: end;
    grid-column-gap: 1rem;
    grid-template: 1fr 1fr / auto 1fr;
    ${({ discountApplied }) =>
      !discountApplied &&
      css`
        align-items: center;
        grid-template: 1fr / auto 1fr;
      `};
    position: fixed;
    bottom: 1rem;
    grid-row-gap: 0.2rem;
  }
`;

const InputRow = styled.div`
  margin: 10px 0;
  display: grid;
  grid-template-columns: auto 30px;
  grid-column-gap: 10px;
`;

const ValidateButton = styled.button.attrs({
  className: 'DiscountScreen-Button',
})`
  appearance: none;
  border: 0;
  border-radius: 3px;
  background: #c8c8c8;
  color: #fff;
  height: 33px;
  width: 33px;
  display: flex;
  align-items: center;
  justify-content: center;

  transition: background-color 0.2s ease-in-out;

  svg {
    width: 7px;
  }

  ${(props) =>
    props.success &&
    css`
      background: #50b83c;
      cursor: pointer;
    `}
`;

export const DiscountLabel = styled(
  ({ className, discountCode, onClick, handleCancel }) => {
    if (!discountCode) return null;
    return (
      <div
        onClick={onClick}
        tabIndex={handleCancel ? undefined : 0}
        onKeyPress={handleConfirmation(onClick)}
        className={`${className} Giftbox-Discount-Code`}
      >
        <DiscountIcon className={'Discount-Icon'} />
        {discountCode}
        {handleCancel && <DiscountClose size={10} onClick={handleCancel} />}
      </div>
    );
  },
)`
  display: inline-flex;
  align-items: center;
  border-radius: 3px;
  border: solid 1px #d6f2de;
  background-color: #f7fcf6;
  padding: 8px 10px;

  ${(props) =>
    props.onClick &&
    css`
      cursor: pointer;
    `}

  .Discount-Icon {
    width: 14px;
    height: 14px;
    margin-right: 10px;
    color: #50b83c;
  }

  span {
    margin-left: 20px;
  }

  span > svg {
    width: 10px;
    height: 10px;
    cursor: pointer;
  }
`;

export default function ValidateDiscountCode({
  discountCode,
  onDiscountCodeChanged,
  setCurrentScreen,
  settings,
}) {
  const [inputValue, setInputValue] = useState('');
  const [state, setState] = useState(discountCode ? 'success' : 'idle');
  const { cart } = useContext(CartContext);

  const totalSpending = getTotalSpending(cart);
  const discount = getDiscount(cart, totalSpending);
  const renderDiscountInfo = settings.discount_adjust_spending && cart;

  const handleCancel = async () => {
    deleteCookie('discount_code');
    setInputValue('');
    await onDiscountCodeChanged('');
    logInfo('dispatching giftbox-discount-removed');
    document.dispatchEvent(new CustomEvent('giftbox-discount-removed'));
    setState('idle');
  };

  const validateDiscount = async () => {
    setState('loading');
    try {
      const discountCodeValid = await validateDiscountCode(inputValue);
      if (discountCodeValid.active) {
        setInputValue('');
        fetch(`/discount/${inputValue}`, {
          credentials: 'same-origin',
          redirect: 'manual',
        });
        await onDiscountCodeChanged(inputValue);
        setState('success');
        logInfo('dispatching giftbox-discount-added');
        document.dispatchEvent(
          new CustomEvent('giftbox-discount-added', {
            detail: { discountCode: inputValue },
          }),
        );
      } else {
        setState('error');
      }
    } catch (error) {
      setState('error');
    }
  };

  function handleChange(event) {
    setInputValue(event.target.value);
  }

  function applyFnToActionableElements(cb) {
    document.querySelectorAll('.giftbox-checkout-cloned').forEach(cb);
  }

  function disableActionableElements() {
    applyFnToActionableElements((element) => (element.disabled = true));
  }

  function enableActionableElements() {
    applyFnToActionableElements((element) => (element.disabled = false));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    disableActionableElements();
    await validateDiscount();
    enableActionableElements();
  }

  const handleClose = () => {
    setCurrentScreen('offers');
    // Resetting focus
    document.activeElement.blur();
  };

  return (
    <DiscountCodeStyle onSubmit={handleSubmit}>
      <Heading handleClose={handleClose}>
        {settings.discount_page_header}
      </Heading>
      <DiscountContent discountApplied={state === 'success'}>
        <InputRow>
          <Input
            type="text"
            value={inputValue}
            onChange={handleChange}
            hasError={state === 'error'}
            disabled={state === 'loading'}
          />
          <ValidateButton
            type="submit"
            disabled={state === 'loading' || !inputValue}
            success={inputValue && inputValue.length > 0}
          >
            {state === 'loading' ? <Spinner /> : <Arrow />}
          </ValidateButton>
        </InputRow>

        <DiscountLabel
          discountCode={discountCode}
          handleCancel={handleCancel}
        />

        {state === 'error' && (
          <p style={{ color: '#e53b3a' }}>{settings.discount_incorrect_info}</p>
        )}

        {state === 'success' && <p>{settings.discount_correct_info}</p>}
        {renderDiscountInfo && (
          <DiscountInfo
            discountApplied={state === 'success'}
            discount={discount}
            spending={totalSpending}
          ></DiscountInfo>
        )}
      </DiscountContent>
    </DiscountCodeStyle>
  );
}
